import { useRouter } from "next/router";
import { useMemo } from "react";
import { useData } from "../../hooks/useData";
import { initializeContentLink } from "./init";
import { LinkableContent, ContentLinkOptions } from "./types";
import { sentryLogger } from "@robe/core";

interface TransitionOptions {
  shallow?: boolean;
  scroll?: boolean;
}

export const useContentLink = () => {
  const { push: routerPush, replace: routerReplace } = useRouter();
  const { config, core, resolvedDomains, contentTypes } = useData();

  return useMemo(() => {
    const link = (content: LinkableContent, { queries, locale, ...trans }: ContentLinkOptions & TransitionOptions = {}) => {
      const { as, href } = initializeContentLink({ config, core, resolvedDomains, contentType: contentTypes[content.type] });

      sentryLogger({
        type: "debug",
        debugBlock: "routing",
        message: "routing link init",
        context: {
          content,
          href: href(content, { queries, locale }),
          as: as(content, { queries, locale }),
          queries,
          locale,
          trans,
        },
      });

      return [href(content, { queries, locale }) ?? "/", as(content, { queries, locale }) ?? undefined, { locale, ...trans }] as const;
    };

    const push = (content: LinkableContent, options: ContentLinkOptions & TransitionOptions = {}) => routerPush(...link(content, options));

    const replace = (content: LinkableContent, options: ContentLinkOptions & TransitionOptions = {}) => {
      routerReplace(...link(content, options));
    };

    const href = (content: LinkableContent, options?: ContentLinkOptions) => {
      const { href: initHref } = initializeContentLink({ config, core, resolvedDomains, contentType: contentTypes[content.type] });
      return initHref(content, options);
    };

    const as = (content: LinkableContent, options?: ContentLinkOptions) => {
      const { as: initAs } = initializeContentLink({ config, core, resolvedDomains, contentType: contentTypes[content.type] });
      return initAs(content, options);
    };

    return {
      /**
       * Creates url path for content that corresponds to next.js routes in pages folder.
       *
       * @example
       * - `/product/[id]?id=4887`
       * - `/user/registration/activation`
       */
      href,
      /**
       * Creates full url for content that corresponds to routes defined in DB.
       * Locally still resolved as path only, because host for local use is not defined in config.
       *
       * @example
       * - `https://example.com/painte`
       * - `https://example.com/registration/activation`
       */
      as,
      link,
      /**
       * Redirects user to created content url path.
       */
      push,
      replace,
    };
  }, [core, config, contentTypes, resolvedDomains, routerPush, routerReplace]);
};
